// The Kierki table (.ai/kierki.md, the owner's pick from the prototype on branch prototype/kierki): on a computer the
// classic table (A: the computer players' backs left / top / right, the trick as a cross on a felt, the hand fanned at
// the bottom), on a phone the compact one (C: the opponents as tiles, the trick as a row of 4 named places, big cards
// in two rows); the end of a deal opens the score window (B's table, in the Win window's look). Tap or click only.
// A played card flies from its player's hand to the trick; the full trick slides to the player who takes it.
import { useLayoutEffect, useRef, useSyncExternalStore, type CSSProperties } from 'react';
import { t } from '../i18n';
import { QuickSay, SayBubble } from '../online/QuickSay';
import { NextDealNote } from '../online/NextDealNote';
import { SeatAvatar, seatFlag as seatFlagOf } from '../online/SeatAvatar';
import { flagged } from '../online/PlayerName';
import { backSrc, backSrcSet, faceSrc, faceSrcSet } from '../options/assets';
import { uiStore, useUiState, type WindowInstance } from '../store/uiStore';
import { useController } from '../store/gameStore';
import { isShortGame, reloadForNewGame } from './nextGame';
import { AdSlot } from '../windows/AdSlot';
import { WindowFrame } from '../windows/WindowFrame';
import { useFitWindow } from '../windows/useFitWindow';
import { cardKey, fileId, leaders, legalCards, sameCard, type HCard, type HeartsState, type Played } from './rules';
import { COLLECT_MS, DEAL_FLIGHT_MS, DEAL_STEP_MS, SLOW, SWAP_HOLD_MS, SWAP_IN_AT, SWAP_IN_MS, SWAP_OUT_MS, SWAP_SLIDE_MS, heartsStore, useHearts } from './store';
import { CrownIcon, LeadIcon, SuitIcon, withSuits } from './suits';
import { fixedNames } from './names';
import { SeatWait, TurnClock } from '../online/TurnClock';
import { mpStore, useMultiplayerState } from '../multiplayer/store';
import './hearts.css';

/** A sentence with each real player's flag beside the name (owner 2026-11-01); not the hint over the hand, which has the
 *  suits only (owner 2026-11-02: "usuń stąd flagę użytkownika"). */
const flaggedText = (text: string) => flagged(text, heartsStore().getState().names);

const SYM = { C: '♣', D: '♦', S: '♠', H: '♥' } as const;
const SEATS_N = 4;
const PHONE = '(max-width: 720px)';
const FLY_MS = 340;
/** The hand closing up round a card that left it (or opening for one that came back). */
const CLOSE_MS = 450;

export const seatName = (seat: number): string => (seat === 0 ? t('hearts.you') : (heartsStore().getState().names ?? fixedNames(t)).name[seat]!);
/** Online: the seat's flag badge (a real player), else null (online/SeatAvatar.tsx). */
const seatFlag = (seat: number): string | null => seatFlagOf(heartsStore().getState().names, seat);
/** A computer player's name as it reads in "cards from …" (names.ts). */
const seatFrom = (seat: number): string => (heartsStore().getState().names ?? fixedNames(t)).from[seat]!;

/** New game after a finished game: a full reload, as the solitaires' Win window does (it refreshes the ads, owner
 *  2026-10-26: at the end of every game, won or lost); after a game under 2 minutes the next one at once (owner
 *  2026-10-27, nextGame.ts). */
function useNewGame(): () => void {
  const ctrl = useController();
  const { startedAt, endedAt } = useHearts();
  return () => (isShortGame(startedAt, endedAt) ? void ctrl.newGame() : reloadForNewGame('hearts'));
}

export function usePhone(): boolean {
  return useSyncExternalStore(
    (notify) => {
      const m = matchMedia(PHONE);
      m.addEventListener?.('change', notify);
      return () => m.removeEventListener?.('change', notify);
    },
    () => document.body.id === 'mobile' || matchMedia(PHONE).matches,
  );
}

/** Where the player's card was when it was played: its flight starts there. */
const playedFrom = new Map<string, DOMRect>();
/** A seat's hand on the table (the backs, the tile, the player's plate): a computer's card starts there. */
const seatBox = (seat: number): DOMRect | null => document.querySelector(`.ht [data-src="${seat}"]`)?.getBoundingClientRect() ?? null;
export const centre = (r: DOMRect) => ({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
/** The player's cards on screen just before "Pass" (the hand, and on a phone the 3 pass places): the exchange starts
 *  them from there. */
let beforeSwap = new Map<string, DOMRect>();
const giverOf = (g: HeartsState) => (SEATS_N - g.passOffset) % SEATS_N;

/** A picture for a flying copy: with its 2x file (owner 2026-10-26: the flying cards looked worse than the others). */
export interface Pic {
  readonly src: string;
  readonly srcSet: string | undefined;
}
export const backPic = (back: string): Pic => ({ src: backSrc(back), srcSet: backSrcSet(back) });
export const facePic = (deck: string, c: HCard): Pic => ({ src: faceSrc(deck, fileId(c)), srcSet: faceSrcSet(deck, fileId(c)) });
export const CARD_RATIO = 145 / 109;

/** A flying copy above the whole table (on document.body), laid out at `box`: a copy is laid out at the biggest size
 *  of its flight and only shrinks (a picture blown up by a transform was soft); the picture over the flight shadow. */
export function ghost(box: DOMRect, pic: Pic, ghosts: HTMLElement[]): HTMLElement {
  const el = document.createElement('div');
  el.className = 'ht-ghost';
  Object.assign(el.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` });
  el.style.setProperty('--sh-w', `${box.width}px`);
  const sh = document.createElement('span');
  sh.className = 'ht-sh';
  const img = document.createElement('img');
  img.src = pic.src;
  if (pic.srcSet) img.srcset = pic.srcSet;
  img.alt = '';
  el.append(sh, img);
  document.body.append(el);
  ghosts.push(el);
  return el;
}

/** The flight shadow (as the solitaires' cards in flight, board.css): the `.ht-sh` of `host` rises as it leaves and is
 *  gone as it lands; a flight in two parts (a copy, then the real card) gives each its half. */
export const SHADOW = {
  arc: [{ opacity: 0 }, { opacity: 1, offset: 0.3 }, { opacity: 1, offset: 0.6 }, { opacity: 0 }],
  rise: [{ opacity: 0 }, { opacity: 1 }],
  fall: [{ opacity: 1 }, { opacity: 0 }],
} satisfies Record<string, Keyframe[]>;
export function flightShadow(host: HTMLElement, frames: Keyframe[], timing: KeyframeAnimationOptions): Animation | null {
  const sh = host.querySelector<HTMLElement>('.ht-sh');
  return sh ? sh.animate(frames, { fill: 'backwards', ...timing }) : null;
}

export function Face({ card, deck }: { card: HCard; deck: string }) {
  return <img className="ht-face" src={faceSrc(deck, fileId(card))} srcSet={faceSrcSet(deck, fileId(card))} alt={cardKey(card)} draggable={false} />;
}

/** Online: the seconds left of my move. */
function MyClock() {
  return <TurnClock at={useHearts().deadlineAt} />;
}

function hint(g: HeartsState, swapping: boolean): string {
  if (swapping) return t('hearts.hint.received', { name: seatFrom(giverOf(g)) });
  // online: my 3 cards are given, the others still choose (offline the exchange follows at once)
  if (g.phase === 'pass' && g.passes[0] !== null) return t('hearts.hint.passWait');
  if (g.phase === 'pass') return t('hearts.hint.pass', { dir: t(`hearts.dir.${g.passOffset}`), name: seatName(g.passOffset) });
  if (g.phase === 'trick-done') return t('hearts.hint.trick', { name: seatName(g.winner) });
  if (g.phase !== 'play') return '';
  if (g.turn !== 0) return t('hearts.hint.turn', { name: seatName(g.turn) });
  if (g.firstTrick && !g.trick.length) return t('hearts.hint.lead2c');
  if (g.trick.length) {
    const led = g.trick[0]!.card.s;
    return t(g.hands[0]!.some((c) => c.s === led) ? 'hearts.hint.follow' : 'hearts.hint.void', { suit: SYM[led] });
  }
  return t(g.heartsBroken ? 'hearts.hint.lead' : 'hearts.hint.leadNoHearts');
}

function Taken({ g, seat }: { g: HeartsState; seat: number }) {
  const hearts = g.taken[seat]!.filter((c) => c.s === 'H').length;
  const queen = g.taken[seat]!.some((c) => c.s === 'S' && c.r === 12);
  return (
    <span className="ht-taken" data-empty={!hearts && !queen ? '' : undefined}>
      {hearts ? (
        <span className="ht-taken__h">
          <SuitIcon s="H" /> {hearts}
        </span>
      ) : null}
      {queen ? (
        <span className="ht-taken__q">
          Q<SuitIcon s="S" />
        </span>
      ) : null}
      {!hearts && !queen ? '–' : null}
    </span>
  );
}

const turnOf = (g: HeartsState, seat: number) => g.phase === 'play' && g.turn === seat;

/** A player's name plate: the initial in a circle, the name, the total, what this deal has cost so far. */
function Plate({ g, seat, src }: { g: HeartsState; seat: number; src?: boolean }) {
  return (
    <div className="ht-plate" data-seat={seat} data-turn={turnOf(g, seat) || undefined} data-src={src ? seat : undefined}>
      <SeatAvatar name={seatName(seat)} flag={seatFlag(seat)} />
      <SeatWait seat={seat} />
      <div className="ht-plate__text">
        <b>{seatName(seat)}</b>
        <span>
          {t('hearts.points', { n: g.scores[seat]! })} · <Taken g={g} seat={seat} />
        </span>
      </div>
    </div>
  );
}

/** The player's hand: chosen cards (passing) lift, or leave for the pass places on a phone; cards not allowed dim. */
function Hand({ g, deck, selected, hideSelected }: { g: HeartsState; deck: string; selected: readonly HCard[]; hideSelected: boolean }) {
  const store = heartsStore();
  const legal = legalCards(g, 0);
  const ref = useRef<HTMLDivElement>(null);
  const placed = useRef(new Map<string, { x: number; y: number }>());
  // one card left the hand (played, or on a phone chosen for the pass) or came back: the others slide to their new
  // places (owner 2026-10-26: the hand jumped to centre itself); not for a new deal or the exchange (their own flights:
  // more than one card changes). Layout places (offsetLeft/Top), not the moving picture.
  useLayoutEffect(() => {
    const hand = ref.current;
    if (!hand) return;
    const now = new Map<string, { x: number; y: number }>();
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) now.set(el.dataset.card!, { x: el.offsetLeft, y: el.offsetTop });
    const before = placed.current;
    placed.current = now;
    const gone = [...before.keys()].filter((k) => !now.has(k)).length;
    const came = [...now.keys()].filter((k) => !before.has(k)).length;
    if (gone + came !== 1 || typeof hand.animate !== 'function') return;
    for (const el of hand.querySelectorAll<HTMLElement>('.ht-card')) {
      const was = before.get(el.dataset.card!), is = now.get(el.dataset.card!)!;
      if (!was || (Math.abs(was.x - is.x) < 0.5 && Math.abs(was.y - is.y) < 0.5)) continue;
      // an even slide (owner 2026-10-26: at 260 ms, most of it in the first 80, it was too fast to see)
      el.animate([{ transform: `translate(${was.x - is.x}px, ${was.y - is.y}px)` }, { transform: 'none' }], { duration: CLOSE_MS, easing: 'ease-in-out' });
    }
  });
  return (
    <div ref={ref} className="ht-hand" data-count={g.hands[0]!.length}>
      {g.hands[0]!.map((c) => {
        const sel = selected.some((s) => sameCard(s, c));
        if (sel && hideSelected) return null;
        const can = g.phase === 'pass' ? g.passes[0] === null : legal.some((x) => sameCard(x, c));
        return (
          <button
            key={cardKey(c)}
            type="button"
            className="ht-card"
            data-card={cardKey(c)}
            data-selected={sel || undefined}
            data-off={can ? undefined : ''}
            onClick={(e) => {
              if (g.phase === 'pass') return store.toggleSelect(c);
              playedFrom.set(cardKey(c), e.currentTarget.getBoundingClientRect());
              store.play(c);
            }}
          >
            <span className="ht-sh" aria-hidden="true" />
            <Face card={c} deck={deck} />
          </button>
        );
      })}
    </div>
  );
}

/** A card of the trick: it flies in from its player (FLIP: drawn at its place, animated from where it came from). */
function TrickCard({ p, g, deck, className }: { p: Played; g: HeartsState; deck: string; className: string }) {
  const fly = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = fly.current;
    if (!el || typeof el.animate !== 'function') return;
    const to = el.getBoundingClientRect();
    const from = p.seat === 0 ? (playedFrom.get(cardKey(p.card)) ?? seatBox(0)) : seatBox(p.seat);
    playedFrom.delete(cardKey(p.card));
    if (!from || !to.width) return;
    const a = centre(from), b = centre(to);
    const scale = p.seat === 0 ? from.width / to.width : 0.6;
    el.animate([{ transform: `translate(${a.x - b.x}px, ${a.y - b.y}px) scale(${scale})` }, { transform: 'none' }], { duration: FLY_MS, easing: 'cubic-bezier(.2,.75,.3,1)' });
    flightShadow(el, SHADOW.arc, { duration: FLY_MS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- once, when the card arrives
  return (
    <div className={`ht-trick__slot ${className}`} data-seat={p.seat} data-card={cardKey(p.card)} data-winner={g.phase === 'trick-done' && g.winner === p.seat ? '' : undefined}>
      <div ref={fly} className="ht-trick__fly">
        <span className="ht-sh" aria-hidden="true" />
        <Face card={p.card} deck={deck} />
      </div>
    </div>
  );
}

/** The full trick slides to its taker when the store says so (`collecting`, the last COLLECT_MS of TRICK_MS): a view
 *  timer of its own ran on while the game waited in a hidden tab, and the cards came back and faded late (owner
 *  2026-10-26). */
function useCollect(g: HeartsState | null, collecting: boolean) {
  useLayoutEffect(() => {
    if (!collecting || !g || g.winner < 0) return;
    // the player's tricks go down to the player's own cards (owner 2026-10-26), not to the name plate
    const to = g.winner === 0 ? (document.querySelector('.ht .ht-hand')?.getBoundingClientRect() ?? seatBox(0)) : seatBox(g.winner);
    if (!to) return;
    const b = centre(to);
    for (const el of document.querySelectorAll<HTMLElement>('.ht .ht-trick__fly')) {
      if (typeof el.animate !== 'function') continue;
      const a = centre(el.getBoundingClientRect());
      el.animate([{ transform: 'none', opacity: 1 }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(0.45)`, opacity: 0 }], { duration: COLLECT_MS, easing: 'ease-in', fill: 'forwards' });
    }
  }, [collecting]); // eslint-disable-line react-hooks/exhaustive-deps -- one per trick
}

/** The deal (owner 2026-10-26, "as in the other solitaires"): a face-down deck lies in the middle of the table and
 *  its 52 cards are thrown off its top to the players one after the other, from the one on the dealer's left round
 *  the table, the deck lower and lower until it is gone (owner 2026-10-26: the cards came out of an empty field). The
 *  player's cards and (on a computer) the computer players' backs fly to their places; on a phone the opponents'
 *  cards fly to their tiles and vanish there. The taps wait for the end (data-dealing). */
const DECK_LAYERS = 8;

/** Calls `end` when every flight has landed. A safety timer covers a flight that never reports its end; when it fires
 *  while cards still fly it waits again (the owner's bot, 2026-10-26: a busy WebKit started the exchange's flights
 *  late, the timer ended the exchange with the received cards still in the air, and taps were let through). Returns
 *  the cancel of the timer. */
export function whenLanded(flights: Animation[], ms: number, end: () => void): () => void {
  void Promise.all(flights.map((a) => a.finished.catch(() => undefined))).then(end);
  let id = 0;
  const check = () => {
    if (flights.some((a) => a.playState === 'running' || a.pending)) id = window.setTimeout(check, 500 * SLOW);
    else end();
  };
  id = window.setTimeout(check, ms * SLOW);
  return () => window.clearTimeout(id);
}
function useDealAnimation(deal: number, back: string): void {
  useLayoutEffect(() => {
    const root = document.querySelector<HTMLElement>('.ht');
    const midEl = document.querySelector<HTMLElement>('.ht .ht-felt, .ht .ht-trick--row');
    if (!deal || !root || !midEl || typeof root.animate !== 'function') return;
    // on a phone the middle of the places (the pass places' label stays readable above the deck)
    const places = [...midEl.querySelectorAll('.ht-place')].map((el) => el.getBoundingClientRect());
    const from = places.length
      ? { x: (Math.min(...places.map((r) => r.left)) + Math.max(...places.map((r) => r.right))) / 2, y: (Math.min(...places.map((r) => r.top)) + Math.max(...places.map((r) => r.bottom))) / 2 }
      : centre(midEl.getBoundingClientRect());
    const ghosts: HTMLElement[] = [];
    const flights: Animation[] = [];
    // the deck: a few backs a little apart, the top one goes first; drawn under the flying cards (in the table, before
    // the hand), the felt under it
    const deckW = (root.querySelector('.ht-hand .ht-card')?.getBoundingClientRect().width ?? 80) * 0.9;
    const deckH = deckW * CARD_RATIO;
    const deck = document.createElement('div');
    deck.className = 'ht-deck';
    Object.assign(deck.style, { left: `${from.x - deckW / 2}px`, top: `${from.y - deckH / 2}px`, width: `${deckW}px`, height: `${deckH}px` });
    for (let k = 0; k < DECK_LAYERS; k++) {
      const img = document.createElement('img');
      img.src = backSrc(back);
      const set = backSrcSet(back);
      if (set) img.srcset = set;
      img.alt = '';
      Object.assign(img.style, { left: `${-k * 0.7}px`, top: `${-k * 0.7}px` });
      deck.append(img);
      // the layer goes when its share of the 52 has left (the bottom one with the last card)
      const leaves = Math.round(((DECK_LAYERS - k) / DECK_LAYERS) * 52) - 1;
      img.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 1, delay: leaves * DEAL_STEP_MS, fill: 'forwards' });
    }
    if (midEl.classList.contains('ht-felt')) midEl.after(deck);
    else midEl.prepend(deck);
    const top = { x: from.x - (DECK_LAYERS - 1) * 0.7, y: from.y - (DECK_LAYERS - 1) * 0.7 };
    /** A copy thrown off the deck to `to` (laid out at the deck's size over it, shrinking to it on the way). */
    const throwTo = (to: DOMRect, order: number, vanish: boolean): HTMLElement => {
      const c = centre(to);
      const g = ghost(new DOMRect(c.x - deckW / 2, c.y - deckH / 2, deckW, deckH), backPic(back), ghosts);
      const timing = { duration: DEAL_FLIGHT_MS, delay: order * DEAL_STEP_MS };
      const flight = g.animate(
        [
          // hidden until it leaves the deck
          { transform: `translate(${top.x - c.x}px, ${top.y - c.y}px)`, opacity: 0 },
          { opacity: 1, offset: 0.01 },
          { transform: `scale(${to.width / deckW})`, opacity: vanish ? 0 : 1 },
        ],
        // off the deck gently, fastest half way, soft at the player (a fast start hid where it came from)
        { ...timing, easing: 'cubic-bezier(.45,.05,.35,1)', fill: 'backwards' },
      );
      flightShadow(g, SHADOW.arc, timing);
      flights.push(flight);
      // gone once landed (the real back shows there; on a phone the copy stood over the tile's name)
      void flight.finished.then(() => g.remove(), () => g.remove());
      return g;
    };
    // the order a dealer throws: seat 1 (left), 2, 3, then the player, and round again
    const order = (seat: number, i: number) => i * 4 + ((seat + 3) % 4);
    // the player's cards leave the deck face down and turn over half way (owner 2026-10-26), as in the exchange
    const deckTop = new DOMRect(top.x - deckW / 2, top.y - deckH / 2, deckW, deckH);
    document.querySelectorAll<HTMLElement>('.ht .ht-hand .ht-card').forEach((el, i) => {
      const to = el.getBoundingClientRect();
      const delay = order(0, i) * DEAL_STEP_MS;
      flipFlight(deckTop, to, backPic(back), null, delay, DEAL_FLIGHT_MS, false, ghosts, flights);
      const m = midway(deckTop, to), b = centre(to);
      const edge = `translate(${m.x - b.x}px, ${m.y - b.y}px) scale(0, ${m.w / to.width})`;
      const second = { duration: DEAL_FLIGHT_MS / 2, delay: delay + DEAL_FLIGHT_MS / 2 };
      flights.push(el.animate([{ transform: edge }, { transform: 'none' }], { ...second, easing: 'ease-out', fill: 'backwards' }));
      flightShadow(el, SHADOW.fall, second);
    });
    for (const seat of [1, 2, 3]) {
      const backs = [...document.querySelectorAll<HTMLElement>(`.ht .ht-backs[data-src="${seat}"] .ht-back`)];
      if (backs.length) {
        // a copy of each back flies above the whole table, the real back shows when its copy lands (owner 2026-10-26:
        // the backs themselves flew under the darkened field and the deck, and seemed to start at the field's edge)
        backs.forEach((el, i) => {
          throwTo(el.getBoundingClientRect(), order(seat, i), false);
          flights.push(el.animate([{ opacity: 0 }, { opacity: 0 }], { duration: order(seat, i) * DEAL_STEP_MS + DEAL_FLIGHT_MS }));
        });
        continue;
      }
      // the phone: no backs on the table, the cards go to the player's tile
      const tile = document.querySelector(`.ht [data-src="${seat}"]`)?.getBoundingClientRect();
      if (!tile) continue;
      const box = new DOMRect(tile.left + tile.width / 2 - 20, tile.top + tile.height / 2 - 26, 40, 53);
      for (let i = 0; i < 13; i++) throwTo(box, order(seat, i), true);
    }
    // the deal is over when the last card has landed (a timer ran ahead of the flights in WebKit)
    root.dataset.dealing = '';
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      delete root.dataset.dealing;
      deck.remove();
    };
    const cancel = whenLanded(flights, 51 * DEAL_STEP_MS + DEAL_FLIGHT_MS + 1000, end);
    return () => {
      cancel();
      end();
      for (const g of ghosts) g.remove();
    };
  }, [deal]); // eslint-disable-line react-hooks/exhaustive-deps -- one per deal
}

/** Half way along a flight from `from` to `to` (a little higher): where the card is edge-on, turning over. */
export const midway = (from: DOMRect, to: DOMRect) => {
  const a = centre(from), b = centre(to);
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - 20, w: (from.width + to.width) / 2 };
};

/** A card flying from `from` to `to` above the table, turned over half way (`first` picture, then `second`; no
 *  `second`: only the first half, the real card flies on from there). The copies are laid out at the bigger of the
 *  two sizes and only shrink; each half carries its part of the flight shadow. */
export function flipFlight(from: DOMRect, to: DOMRect, first: Pic, second: Pic | null, delay: number, duration: number, vanish: boolean, ghosts: HTMLElement[], flights: Animation[]) {
  const a = centre(from), b = centre(to), m = midway(from, to);
  const w = Math.max(from.width, to.width);
  const box = new DOMRect(a.x - w / 2, a.y - (w * CARD_RATIO) / 2, w, w * CARD_RATIO);
  const start = `scale(${from.width / w})`;
  const half = `translate(${m.x - a.x}px, ${m.y - a.y}px) scale(0, ${m.w / w})`;
  const firstTiming = { duration: duration / 2, delay };
  const secondTiming = { duration: duration / 2, delay: delay + duration / 2 };
  // hidden until its flight starts (a card waiting its turn lay on the deck, or by the giver's fan)
  const one = ghost(box, first, ghosts);
  one.style.opacity = '0';
  const out = one.animate([{ transform: start, opacity: 1 }, { transform: half, opacity: 1 }], { ...firstTiming, easing: 'ease-in', fill: 'forwards' });
  flightShadow(one, SHADOW.rise, firstTiming);
  flights.push(out);
  if (!second) {
    void out.finished.then(() => one.remove(), () => one.remove());
    return;
  }
  const two = ghost(box, second, ghosts);
  const end = two.animate([{ transform: half }, { transform: `translate(${b.x - a.x}px, ${b.y - a.y}px) scale(${to.width / w})`, opacity: vanish ? 0 : 1 }], { ...secondTiming, easing: 'ease-out', fill: 'both' });
  flightShadow(two, SHADOW.fall, secondTiming);
  flights.push(end);
  // gone on landing: the real card takes its place there (else the real card slid down under its copy)
  const gone = () => {
    one.remove();
    two.remove();
  };
  void end.finished.then(gone, gone);
}

/** The exchange after "Pass" (owner 2026-10-26: no mark on the received cards, show them come): the player's 3 cards
 *  fly face down to their taker, the giver's 3 fly in from the giver and turn face up, wait above their places in the
 *  hand, and slide down as the other cards close up. Taps wait (data-dealing); the store holds the computer players
 *  for SWAP_MS. */
function useExchangeAnimation(swap: number, g: HeartsState | null, deck: string, back: string): void {
  useLayoutEffect(() => {
    const root = document.querySelector<HTMLElement>('.ht');
    const before = beforeSwap;
    beforeSwap = new Map();
    if (!swap || !g || !root || typeof root.animate !== 'function') return;
    const ghosts: HTMLElement[] = [];
    const flights: Animation[] = [];
    const total = SWAP_IN_AT + SWAP_IN_MS + SWAP_HOLD_MS + SWAP_SLIDE_MS;
    const landed = (SWAP_IN_AT + SWAP_IN_MS) / total;
    const turned = (SWAP_IN_AT + SWAP_IN_MS / 2) / total;
    const slide = (SWAP_IN_AT + SWAP_IN_MS + SWAP_HOLD_MS) / total;
    const small = (r: DOMRect, w: number) => {
      const c = centre(r);
      const h = w * CARD_RATIO;
      return new DOMRect(c.x - w / 2, c.y - h / 2, w, h);
    };
    const cardW = document.querySelector('.ht .ht-hand .ht-card')?.getBoundingClientRect().width ?? 80;
    // out: the player's 3, face down to the player they are given to
    const taker = seatBox(g.passOffset);
    for (const c of g.passes[0] ?? []) {
      const from = before.get(cardKey(c));
      if (from && taker) flipFlight(from, small(taker, cardW * 0.55), facePic(deck, c), backPic(back), 0, SWAP_OUT_MS, true, ghosts, flights);
    }
    // in: the giver's 3 come face up to just above their places, wait, and slide down
    const giver = seatBox(giverOf(g));
    for (const c of g.received[0]!) {
      const el = root.querySelector<HTMLElement>(`.ht-hand .ht-card[data-card="${cardKey(c)}"]`);
      if (!el || !giver) continue;
      const to = el.getBoundingClientRect();
      const lift = to.height * 0.32;
      const hover = new DOMRect(to.left, to.top - lift, to.width, to.height);
      // the back flies from the giver to half way; there the real card, face up, flies on to its place, in the hand's
      // own order over and under its neighbours (owner 2026-10-26: a copy above the hand jumped under them on landing)
      const start = small(giver, to.width * 0.55);
      flipFlight(start, hover, backPic(back), null, SWAP_IN_AT, SWAP_IN_MS, false, ghosts, flights);
      const m = midway(start, hover), b = centre(to);
      const edge = `translate(${m.x - b.x}px, ${m.y - b.y}px) scale(0, ${m.w / to.width})`;
      const up = `translateY(${-lift}px)`;
      flights.push(
        el.animate(
          [
            { transform: edge, opacity: 0 },
            { transform: edge, opacity: 0, offset: turned - 0.001 },
            { transform: edge, opacity: 1, offset: turned, easing: 'ease-out' },
            { transform: up, offset: landed },
            { transform: up, offset: slide, easing: 'ease-in-out' },
            { transform: 'none' },
          ],
          { duration: total, fill: 'backwards' },
        ),
      );
      // its shadow from half way, held while it waits lifted above the hand, gone as it slides in
      flightShadow(el, [{ opacity: 0 }, { opacity: 0, offset: turned - 0.001 }, { opacity: 1, offset: turned }, { opacity: 1, offset: slide }, { opacity: 0 }], { duration: total });
    }
    // the other cards keep their places until the slide, then close up (FLIP from where they were)
    for (const el of root.querySelectorAll<HTMLElement>('.ht-hand .ht-card')) {
      const key = el.dataset.card!;
      if (g.received[0]!.some((c) => cardKey(c) === key)) continue;
      const was = before.get(key);
      if (!was) continue;
      const now = el.getBoundingClientRect();
      const d = `translate(${was.left - now.left}px, ${was.top - now.top}px)`;
      if (Math.abs(was.left - now.left) < 0.5 && Math.abs(was.top - now.top) < 0.5) continue;
      flights.push(el.animate([{ transform: d }, { transform: d, offset: slide }, { transform: 'none' }], { duration: total, easing: 'ease-in-out', fill: 'backwards' }));
    }
    // the cards not allowed now dim while they slide in, not at once (owner 2026-10-26: the received ones flew in bright
    // and darkened suddenly); the same filters as hearts.css, one filter on the picture (iPad Safari)
    const shadow = 'drop-shadow(0 1px 1.5px rgb(0 0 0 / 30%))';
    for (const face of root.querySelectorAll<HTMLElement>('.ht-hand .ht-card[data-off] .ht-face')) {
      flights.push(
        face.animate([{ filter: `brightness(1) ${shadow}` }, { filter: `brightness(1) ${shadow}`, offset: slide }, { filter: `brightness(0.6) ${shadow}` }], {
          duration: total,
          fill: 'backwards',
        }),
      );
    }
    root.dataset.dealing = '';
    let over = false;
    const end = () => {
      if (over) return;
      over = true;
      delete root.dataset.dealing;
      for (const el of ghosts) el.remove();
    };
    const cancel = whenLanded(flights, total + 1000, end);
    return () => {
      cancel();
      end();
    };
  }, [swap]); // eslint-disable-line react-hooks/exhaustive-deps -- one per exchange
}

function PassButton({ g, selected }: { g: HeartsState; selected: readonly HCard[] }) {
  if (g.phase !== 'pass') return null;
  return (
    <button
      type="button"
      className="ht-go"
      id="hearts_pass"
      disabled={selected.length !== 3}
      onClick={() => {
        beforeSwap = new Map([...document.querySelectorAll<HTMLElement>('.ht .ht-hand .ht-card, .ht .ht-place .ht-card')].map((el) => [el.dataset.card!, el.getBoundingClientRect()]));
        heartsStore().pass();
      }}
    >
      {/* no direction and no arrow (owner 2026-10-26: "w lewo" with an arrow to the right); the hint names the player */}
      {t('hearts.button.pass')}
    </button>
  );
}

function NextButton({ g }: { g: HeartsState }) {
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  // the score window closed without its button: the next deal from the table (offline only: online the server deals
  // and a new game would reload the page)
  if (online || (g.phase !== 'hand-end' && g.phase !== 'over')) return null;
  if (uiStore().isOpen('hearts_score')) return null;
  return (
    <button type="button" className="ht-go" onClick={() => (g.phase === 'over' ? newGame() : heartsStore().nextHand())}>
      {t(g.phase === 'over' ? 'hearts.score.newGame' : 'hearts.score.next')}
    </button>
  );
}

function Backs({ g, seat, back }: { g: HeartsState; seat: number; back: string }) {
  // every computer player's cards stand upright, fanned from left to right (owner 2026-10-26)
  return (
    <div className="ht-backs" data-src={seat} aria-hidden="true">
      {g.hands[seat]!.map((_, i) => (
        <img key={i} className="ht-back" src={backSrc(back)} srcSet={backSrcSet(back)} alt="" draggable={false} />
      ))}
    </div>
  );
}

/** A: the classic table (a computer). */
function ClassicTable({ g, deck, back, selected, swapping }: { g: HeartsState; deck: string; back: string; selected: readonly HCard[]; swapping: boolean }) {
  return (
    <div className="ht-a">
      <div className="ht-seat ht-seat--top">
        <Plate g={g} seat={2} />
        <Backs g={g} seat={2} back={back} />
      </div>
      <div className="ht-seat ht-seat--left">
        <Plate g={g} seat={1} />
        <Backs g={g} seat={1} back={back} />
      </div>
      <div className="ht-seat ht-seat--right">
        <Plate g={g} seat={3} />
        <Backs g={g} seat={3} back={back} />
      </div>
      <div className="ht-mid">
        <div className="ht-felt" aria-hidden="true" />
        <div className="ht-trick ht-trick--cross">
          {g.trick.map((p) => (
            <TrickCard key={`${g.handNo}-${cardKey(p.card)}`} p={p} g={g} deck={deck} className={`ht-trick__slot--s${p.seat}`} />
          ))}
        </div>
      </div>
      <div className="ht-hintrow">
        <span className="ht-hint" data-hint="">
          {withSuits(hint(g, swapping))}
          <MyClock />
        </span>
        <PassButton g={g} selected={selected} />
        <NextButton g={g} />
      </div>
      <div className="ht-me">
        <Plate g={g} seat={0} src />
      </div>
      <Hand g={g} deck={deck} selected={selected} hideSelected={false} />
    </div>
  );
}

/** C: compact, big cards (a phone). */
function CompactTable({ g, deck, selected, swapping }: { g: HeartsState; deck: string; selected: readonly HCard[]; swapping: boolean }) {
  const start = g.trick.length ? g.trick[0]!.seat : Math.max(0, g.turn);
  const order = [0, 1, 2, 3].map((k) => (start + k) % 4);
  return (
    <div className="ht-c">
      <div className="ht-chips">
        {[1, 2, 3].map((p) => (
          <div key={p} className="ht-chip" data-seat={p} data-turn={turnOf(g, p) || undefined} data-src={p}>
            <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
            <SeatWait seat={p} />
            <b>{seatName(p)}</b>
            <small>{t('hearts.cards', { n: g.hands[p]!.length })}</small>
            <span>
              {t('hearts.points', { n: g.scores[p]! })} · <Taken g={g} seat={p} />
            </span>
          </div>
        ))}
      </div>
      <div className="ht-trick ht-trick--row">
        {g.phase === 'pass' ? <div className="ht-passfor">{flaggedText(t('hearts.for', { name: seatName(g.passOffset) }))}</div> : null}
        {g.phase === 'pass'
          ? [0, 1, 2].map((k) => (
              <div key={k} className="ht-place">
                {selected[k] ? (
                  <button type="button" className="ht-card" data-card={cardKey(selected[k]!)} data-chosen="" onClick={() => heartsStore().toggleSelect(selected[k]!)}>
                    <Face card={selected[k]!} deck={deck} />
                  </button>
                ) : (
                  <div className="ht-place__empty" />
                )}
              </div>
            ))
          : order.map((p, k) => {
              const played = g.trick.find((x) => x.seat === p);
              return (
                <div key={p} className="ht-place" data-lead={k === 0 || undefined}>
                  <span>
                    {/* a phone: the name alone over its place, no flag (owner 2026-11-02: "na mobilkach flagi nie renderuj, aby zwolnić miejsce") */}
                    {seatName(p)}
                    {k === 0 ? <LeadIcon /> : null}
                  </span>
                  {played ? <TrickCard key={`${g.handNo}-${cardKey(played.card)}`} p={played} g={g} deck={deck} className="" /> : <div className="ht-place__empty" />}
                </div>
              );
            })}
      </div>
      {/* a slot of two lines: a longer or shorter hint no longer moves the table above (owner 2026-11-02) */}
      <div className="ht-msgslot">
        <div className="ht-msg">
          <span className="ht-hint" data-hint="">
            {withSuits(hint(g, swapping))}
            <MyClock />
          </span>
          <PassButton g={g} selected={selected} />
          <NextButton g={g} />
        </div>
      </div>
      <div className="ht-mechip" data-seat={0} data-turn={turnOf(g, 0) || undefined} data-src={0}>
        {seatName(0)} · {t('hearts.points', { n: g.scores[0]! })} · <Taken g={g} seat={0} />
      </div>
      <Hand g={g} deck={deck} selected={selected} hideSelected />
    </div>
  );
}

export function HeartsTable() {
  const { game: g, selected, deal, collecting, swap, swapping } = useHearts();
  const skin = useUiState().options.skin;
  const phone = usePhone();
  useCollect(g, collecting);
  useDealAnimation(deal, skin.cardBackType);
  useExchangeAnimation(swap, g, skin.deckType, skin.cardBackType);
  return (
    <div id="main" className="board ht" data-board="" data-game="hearts" data-layout={phone ? 'compact' : 'classic'} data-phase={g?.phase} data-deck={skin.deckType}>
      {g ? phone ? <CompactTable g={g} deck={skin.deckType} selected={selected} swapping={swapping} /> : <ClassicTable g={g} deck={skin.deckType} back={skin.cardBackType} selected={selected} swapping={swapping} /> : null}
    </div>
  );
}

/** The hero of the score window: the cards that cost points, fanned (as the Win window's royal flush). */
const HERO = ['Qc', '10h', 'Jh', 'Qh', 'Kh'];

/** The end of a deal or of the game: the points of every deal (B's table), the totals with a crown on the lowest,
 *  "how close to 100", the moon, the winner; in the Win window's look (cream, the back as a faint wallpaper). */
/** The score window shrinks its content step by step until nothing scrolls (owner 2026-10-26: a scroll bar on a
 *  1440 x 685 screen with the bottom ad band): data-fit-1 .. data-fit-6 (windows/useFitWindow.ts, hearts.css). */
const FIT_STEPS = 6;

export function HeartsScoreWindow({ instance }: { instance: WindowInstance }) {
  const { game: g } = useHearts();
  const skin = useUiState().options.skin;
  useFitWindow('.lbx_window.lbx_hearts_score', FIT_STEPS, g ? `${g.handNo}-${g.phase}` : null);
  const newGame = useNewGame();
  const online = !!useMultiplayerState().cards;
  if (!g) return null;
  const over = g.phase === 'over';
  const limit = g.limit ?? 100;
  const best = leaders(g.scores);
  const youWin = over && best.includes(0);
  const go = () => {
    if (over) return newGame();
    uiStore().closeWindow('hearts_score');
    heartsStore().nextHand();
  };
  const title = over ? (youWin ? t('hearts.score.youWin') : t('hearts.score.game')) : `${t('hearts.score.deal')} ${g.handNo + 1}`;
  const sub = g.moon >= 0 ? (g.moon === 0 ? t('hearts.score.moonYou') : t('hearts.score.moon', { name: seatName(g.moon) })) : over && !youWin ? t('hearts.score.winner', { name: best.map(seatName).join(', ') }) : '';
  return (
    <WindowFrame name="hearts_score" header={t(over ? 'hearts.score.game' : 'hearts.score.hand')} overlay closing={instance.closing}>
      <div className="tab_contents">
        <div className="hs-backdrop" aria-hidden="true" style={{ '--hs-back': `url("${backSrc(skin.cardBackType)}")` } as CSSProperties} />
        <div className="hs-cont">
          <div className="hs-hero" aria-hidden="true">
            {HERO.map((id, i) => (
              <img key={id} className="hs-hero__card" src={faceSrc(skin.deckType, id)} srcSet={faceSrcSet(skin.deckType, id)} alt="" draggable={false} style={{ '--i': i - 2 } as CSSProperties} />
            ))}
          </div>
          <div className="hs-title" data-win={youWin || undefined}>
            {title}
          </div>
          <div className="hs-sub" data-moon={g.moon >= 0 || undefined}>
            {flaggedText(sub)}
          </div>
          {/* one tile a player (owner 2026-10-26: the list of every deal grew until the window scrolled and the ad and
              the button looked wrong): this deal's points, the total, how close to 100, the crown on the lowest */}
          <div className="hs-tiles">
            {[0, 1, 2, 3].map((p) => {
              const last = g.history[g.history.length - 1]?.[p] ?? 0;
              return (
                <div key={p} className="hs-tile" data-me={p === 0 || undefined} data-best={best.includes(p) || undefined}>
                  <SayBubble seat={p} />
                  <div className="hs-tile__who">
                    <SeatAvatar name={seatName(p)} flag={seatFlag(p)} />
                    <b>{seatName(p)}</b>
                    {best.includes(p) ? <CrownIcon /> : null}
                  </div>
                  <div className="hs-tile__deal" data-zero={last === 0 || undefined}>
                    {last ? `+${last}` : '0'}
                  </div>
                  <div className="hs-tile__total">{g.scores[p]}</div>
                  <div className="hs-bar__track">
                    <i style={{ width: `${Math.min(100, (g.scores[p]! / limit) * 100)}%` } as CSSProperties} />
                  </div>
                  <small>{g.scores[p]} / {limit}</small>
                </div>
              );
            })}
          </div>
          <p className="hs-legend">{withSuits(t('hearts.score.columns', { n: g.handNo + 1 }))}</p>
          {online ? (over ? <QuickSay iWon={youWin} /> : <QuickSay key={g.handNo} iWon={false} deal />) : null}
        </div>
        <AdSlot slot={2} />
        {online ? (
          // online: the next deal comes by itself; after the game, the same table again or leave it
          <div className="rules_buttons hs-buttons">
            {over ? (
              <>
                <input type="button" className="mp_btn_secondary" id="mp_cards_leave" value={t('lobby.room.leave')} onClick={() => mpStore().leaveCards()} />
                <input type="button" className="nomoves_shuffle" id="mp_cards_again" value={t('lobby.again')} onClick={() => mpStore().again()} />
              </>
            ) : (
              <NextDealNote />
            )}
          </div>
        ) : (
          <div className="rules_buttons hs-buttons">
            <input type="button" className="nomoves_shuffle" id="hearts_next" value={t(over ? 'hearts.score.newGame' : 'hearts.score.next')} onClick={go} />
          </div>
        )}
      </div>
    </WindowFrame>
  );
}
